import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { setAuthToken } from "@/config/api";

export default function Subscribe() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    confirmPassword: "",
    company_name: user?.company_name || "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const debounceTimer = useRef(null);

  const loadPackage = useCallback(async () => {
    try {
      const data = await api.subscriptions.getPackage(packageId);
      setPackageData(data);
    } catch (error) {
      console.error("Error loading package:", error);
      setError("Package not found");
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  const checkSubscriptionAndRedirect = useCallback(async () => {
    try {
      // Check if user has an active subscription
      const subscription = await api.subscriptions.getCurrent();
      if (
        subscription &&
        subscription.status === "active" &&
        subscription.payment_status === "paid"
      ) {
        // User has active subscription, redirect to dashboard
        navigate("/dashboard", { replace: true });
        return;
      }
    } catch (error) {
      // 404 or "No active subscription found" is expected - not an error
      // Only log if it's a different error
      if (
        !error.message?.includes("No active subscription found") &&
        !error.message?.includes("404") &&
        !error.message?.includes("Not found")
      ) {
        console.error("Error checking subscription:", error);
      }
    }
    // Only load package if we're not redirecting
    setCheckingSubscription(false);
    if (packageId) {
      loadPackage();
    } else {
      setError("Package ID not found in URL");
      setLoading(false);
    }
  }, [loadPackage, navigate, packageId]);

  useEffect(() => {
    if (isAuthenticated) {
      checkSubscriptionAndRedirect();
    } else {
      setCheckingSubscription(false);
      if (packageId) {
        loadPackage();
      } else {
        setError("Package ID not found in URL");
        setLoading(false);
      }
    }
  }, [checkSubscriptionAndRedirect, isAuthenticated, loadPackage, packageId]);

  const validateEmail = useCallback(async (email) => {
    if (!email || !email.includes("@")) {
      setEmailError("");
      return;
    }

    setCheckingEmail(true);
    setEmailError("");

    try {
      const response = await api.users.checkEmail(email);
      if (response.exists) {
        setEmailError("This email is already registered");
      } else {
        setEmailError("");
      }
    } catch (error) {
      console.error("Email validation error:", error);
      // Don't show error on validation failure, just log it
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  const handleEmailChange = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, email: value });
    setEmailError(""); // Clear previous error on change

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced validation
    if (value && value.includes("@")) {
      debounceTimer.current = setTimeout(() => {
        validateEmail(value);
      }, 500);
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setError("");

    if (!packageId) {
      setError("Package ID is missing. Please go back and select a package.");
      return;
    }

    setSubmitting(true);

    try {
      let response;

      if (isAuthenticated) {
        // User is already logged in, just create subscription
        response = await api.subscriptions.create(packageId);
        if (response.subscription?.id) {
          navigate(`/payment/${response.subscription.id}`);
          return;
        } else {
          setError("Subscription created but ID not found");
          return;
        }
      } else {
        // Validate required fields for new users
        if (!formData.name || !formData.email || !formData.password) {
          setError("Please fill in all required fields");
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setError("Passwords do not match");
          return;
        }

        if (formData.password.length < 6) {
          setError("Password must be at least 6 characters");
          return;
        }
        // Check email one more time before submitting
        if (formData.email) {
          const emailCheck = await api.users.checkEmail(formData.email.trim());
          if (emailCheck.exists) {
            setEmailError("This email is already registered");
            setSubmitting(false);
            return;
          }
        }

        // New user registration
        const registrationData = {
          email: formData.email.trim(),
          password: formData.password,
          name: formData.name.trim(),
          company_name: formData.company_name?.trim() || "",
          package_id: packageId,
        };

        console.log("Registering with data:", {
          ...registrationData,
          password: "***",
        });

        response = await api.subscriptions.register(registrationData);

        // Store token (sessionStorage for per-tab login) and navigate to payment
        if (response.token) {
          setAuthToken(response.token);
        }
        if (response.subscription?.id) {
          navigate(`/payment/${response.subscription.id}`);
          return;
        } else {
          setError("Registration successful but subscription ID not found");
          return;
        }
      }
    } catch (error) {
      console.error("Registration/Subscription error:", error);
      if (
        error.message?.includes("Email already registered") ||
        error.message?.includes("email")
      ) {
        setEmailError("This email is already registered");
      } else {
        setError(error.message || "Failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Package not found</p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {isAuthenticated
              ? "Complete Your Subscription"
              : "Create Your Account"}
          </h2>
          <p className="text-gray-600 mb-8">
            You're subscribing to:{" "}
            <span className="font-semibold">{packageData.name}</span>
            {isAuthenticated && (
              <span className="block mt-2 text-sm text-gray-500">
                Logged in as: {user?.email}
              </span>
            )}
            {process.env.NODE_ENV === "development" && (
              <span className="text-xs text-gray-400 ml-2">
                (Package ID: {packageId})
              </span>
            )}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {isAuthenticated ? (
            // For logged-in users, show a simple subscription form
            <div className="space-y-6">
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                <p className="text-sm text-cyan-800">
                  <strong>You're logged in as:</strong> {user?.email}
                </p>
                <p className="text-sm text-cyan-700 mt-1">
                  Click the button below to subscribe to this plan. No
                  additional information needed.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-900">Package:</span>
                  <span className="text-gray-700">{packageData.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-cyan-600">
                    {packageData.currency === "USD"
                      ? "$"
                      : packageData.currency}
                    {packageData.price} /{" "}
                    {packageData.billing_period === "monthly"
                      ? "month"
                      : "year"}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-cyan-600 text-white py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? "Creating Subscription..."
                  : "Subscribe & Continue to Payment"}
              </button>
            </div>
          ) : (
            // For new users, show full registration form
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    onBlur={() =>
                      formData.email && validateEmail(formData.email)
                    }
                    required
                    className={`w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600 ${
                      emailError
                        ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="john@example.com"
                  />
                  {checkingEmail && (
                    <p className="mt-1 text-xs text-gray-500">
                      Checking email...
                    </p>
                  )}
                  {emailError && (
                    <p className="mt-1 text-xs text-red-600">{emailError}</p>
                  )}
                  {!emailError && formData.email && !checkingEmail && (
                    <p className="mt-1 text-xs text-emerald-600">
                      Email is available
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) =>
                    setFormData({ ...formData, company_name: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                  placeholder="Your Company Name"
                />
              </div>

              {!isAuthenticated && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      required
                      minLength={6}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                      minLength={6}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-900">Package:</span>
                  <span className="text-gray-700">{packageData.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-cyan-600">
                    {packageData.currency === "USD"
                      ? "$"
                      : packageData.currency}
                    {packageData.price} /{" "}
                    {packageData.billing_period === "monthly"
                      ? "month"
                      : "year"}
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={
                  submitting ||
                  checkingEmail ||
                  emailError === "This email is already registered"
                }
                className="w-full bg-cyan-600 text-white py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? "Creating Account..."
                  : "Create Account & Continue to Payment"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
